import { useEffect, useRef, useState } from 'react';
import { Carte, Champ, Message, Squelette } from '@journal/ui';

/**
 * Réglages → Général.
 *
 * Reprend `renderGeneral` de l'application d'origine, y compris ses CONTRÔLES :
 * une grille de boutons pour les niveaux, trois cartes pour la zone. Les champs
 * texte que j'avais mis à la place (« niveaux séparés par des virgules », « A, B
 * ou C ») demandaient de connaître une syntaxe et acceptaient n'importe quelle
 * faute de frappe — un « cp » minuscule ou un « CE 1 » avec espace vidait
 * silencieusement tout l'écran Programme.
 *
 * Un bouton ne peut produire qu'une valeur valide. C'est la raison d'être du
 * contrôle choisi par l'original, et c'est pour cela qu'il est repris tel quel.
 */

/** Ordre officiel de la scolarité. Sert au tri comme à l'affichage. */
export const NIVEAUX_DISPONIBLES = [
  'TPS', 'PS', 'MS', 'GS', 'CP', 'CE1', 'CE2', 'CM1', 'CM2',
] as const;

/** Académies les plus représentatives de chaque zone, comme dans l'original. */
export const ZONES = [
  { lettre: 'A', villes: 'Lyon, Grenoble, Bordeaux…' },
  { lettre: 'B', villes: 'Nantes, Rennes, Lille…' },
  { lettre: 'C', villes: 'Paris, Versailles, Créteil…' },
] as const;

/**
 * Ajoute ou retire un niveau, en gardant l'ordre de la scolarité.
 *
 * Le tri n'est pas cosmétique : le libellé « CM1, CP » se lit mal, et surtout
 * l'ordre sert de clé de comparaison ailleurs dans l'application.
 */
export function basculerNiveau(actuels: string[], niveau: string): string[] {
  const suivants = actuels.includes(niveau)
    ? actuels.filter((n) => n !== niveau)
    : [...actuels, niveau];
  return suivants.sort(
    (a, b) => NIVEAUX_DISPONIBLES.indexOf(a as (typeof NIVEAUX_DISPONIBLES)[number])
      - NIVEAUX_DISPONIBLES.indexOf(b as (typeof NIVEAUX_DISPONIBLES)[number]),
  );
}

export function ReglagesGeneral({
  niveaux, zone, vacances, chargementVacances, surNiveaux, surZone,
  classeCourante, modifiable, nomEcole, surNomEcole,
}: {
  niveaux: string[];
  zone: string;
  /** Intitulé affiché de l'école. `null` tant que `/auth/me` n'a pas répondu. */
  nomEcole: string | null;
  surNomEcole: (nom: string) => Promise<void>;
  vacances: Array<{ nom: string; debut: string; fin: string }> | null;
  chargementVacances: boolean;
  surNiveaux: (niveaux: string[]) => void;
  /** Nom de la classe dont on règle les niveaux. `null` = aucune. */
  classeCourante: string | null;
  /** Faux pour un enseignant : c'est la direction qui répartit les niveaux. */
  modifiable: boolean;
  surZone: (zone: string) => void;
}) {
  const dateLongue = (iso: string) =>
    new Date(`${iso}T00:00:00Z`).toLocaleDateString('fr-FR', {
      day: 'numeric', month: 'long', timeZone: 'UTC',
    });

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {/*
        * ── Le nom de l'ÉCOLE ────────────────────────────────────────────────
        * Saisi en trente secondes à l'inscription, relu ensuite sur l'écran
        * Équipe et dans chaque courriel d'invitation. Une coquille, ou un
        * « École » devenu « Groupe scolaire », n'avait aucun endroit où se
        * corriger.
        *
        * Réservé à la direction, comme la zone et les niveaux : ce nom vaut
        * pour tout l'établissement, et il part dans les courriels d'invitation.
        */}
      <NomDeLEcole nom={nomEcole} modifiable={modifiable} surEnregistrement={surNomEcole} />

      {/*
        * La carte disparaît sans classe.
        *
        * Un compte de direction n'en a pas : lui proposer de régler « les
        * niveaux de la classe » n'aurait aucun objet, et l'enregistrement
        * n'aurait nulle part où aller. Les niveaux de chaque classe se règlent
        * alors depuis Comptes et classes.
        */}
      {classeCourante !== null && (
      <Carte titre={`Niveau(x) · ${classeCourante}`}>
        <p className="mb-3 text-[13px] text-[var(--color-fg-muted)]">
          {modifiable
            ? 'Sélectionnez un ou plusieurs niveaux. Ils déterminent le programme proposé à cette classe.'
            : 'Ces niveaux sont fixés par la direction de l’école.'}
        </p>

        {/*
          * `role="group"` et `aria-pressed` : ce sont des bascules, pas des
          * cases d'un formulaire. Un lecteur d'écran annonce « CP, activé ».
          */}
        <div
          role="group"
          aria-label="Niveaux enseignés"
          className="grid grid-cols-3 gap-2 sm:grid-cols-5"
        >
          {NIVEAUX_DISPONIBLES.map((n) => {
            const actif = niveaux.includes(n);
            // Desactives pour un enseignant : c'est la direction qui repartit.
            return (
              <button
                key={n}
                type="button"
                aria-pressed={actif}
                disabled={!modifiable}
                onClick={() => surNiveaux(basculerNiveau(niveaux, n))}
                className={[
                  'rounded-lg px-2 py-2 text-[13px] font-semibold',
                  'transition-colors duration-150 motion-reduce:transition-none',
                  'focus-visible:outline-2 focus-visible:outline-offset-1',
                  'focus-visible:outline-[var(--color-focus)]',
                  actif
                    ? 'bg-[var(--color-primary)] text-[var(--color-primary-fg)]'
                    : 'bg-[var(--color-surface-raised)] text-[var(--color-fg-muted)] '
                      + 'ring-1 ring-inset ring-[var(--color-border)] '
                      + 'hover:bg-[var(--color-surface-hover)] hover:text-[var(--color-fg)]',
                ].join(' ')}
              >
                {n}
              </button>
            );
          })}
        </div>

        <p className="mt-4 rounded-lg bg-[var(--color-surface-raised)] px-3 py-2 text-[13px]">
          <span className="text-[var(--color-fg-muted)]">Niveau(x) : </span>
          <strong className="text-[var(--color-primary)]">
            {niveaux.length > 0 ? niveaux.join(', ') : '–'}
          </strong>
        </p>

        {niveaux.length === 0 && (
          <div className="mt-3">
            <Message ton="attention">
              Sans niveau, le programme officiel s'affiche en entier : vous verrez
              la maternelle et le CM2 mélangés dans Programme et Évaluation.
            </Message>
          </div>
        )}
      </Carte>
      )}

      <Carte titre="Zone scolaire">
        <p className="mb-3 text-[13px] text-[var(--color-fg-muted)]">
          Détermine les dates des vacances scolaires.
        </p>

        <div role="group" aria-label="Zone de vacances" className="flex flex-col gap-2">
          {ZONES.map((z) => {
            const actif = zone === z.lettre;
            return (
              <button
                key={z.lettre}
                type="button"
                aria-pressed={actif}
                disabled={!modifiable}
                onClick={() => surZone(z.lettre)}
                className={[
                  'flex flex-col rounded-lg px-3 py-2 text-left',
                  'transition-colors duration-150 motion-reduce:transition-none',
                  'focus-visible:outline-2 focus-visible:outline-offset-1',
                  'focus-visible:outline-[var(--color-focus)]',
                  actif
                    ? 'bg-[var(--color-primary-soft)] ring-2 ring-inset ring-[var(--color-primary)]'
                    : 'bg-[var(--color-surface-raised)] ring-1 ring-inset '
                      + 'ring-[var(--color-border)] hover:bg-[var(--color-surface-hover)]',
                ].join(' ')}
              >
                <span className={[
                  'text-[13px] font-semibold',
                  actif ? 'text-[var(--color-primary-soft-fg)]' : '',
                ].join(' ')}
                >
                  {`Zone ${z.lettre}`}
                </span>
                <span className="text-[11px] text-[var(--color-fg-muted)]">{z.villes}</span>
              </button>
            );
          })}
        </div>

        <div className="mt-4">
          <h3 className="mb-2">
            {'Vacances '}
            <span className="text-[var(--color-primary)]">{`Zone ${zone}`}</span>
          </h3>
          {chargementVacances && !vacances ? (
            <Squelette className="h-28" />
          ) : !vacances || vacances.length === 0 ? (
            <p className="text-[13px] text-[var(--color-fg-muted)]">
              Calendrier non disponible pour cette année.
            </p>
          ) : (
            <ul className="flex flex-col gap-1">
              {vacances.map((v) => (
                <li
                  key={v.nom}
                  className="flex flex-wrap items-baseline justify-between gap-2 border-b border-[var(--color-border)] pb-1 text-[13px] last:border-0"
                >
                  <span className="font-medium">{v.nom}</span>
                  <span className="text-[var(--color-fg-muted)]">
                    {`${dateLongue(v.debut)} → ${dateLongue(v.fin)}`}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Carte>
    </div>
  );
}


/**
 * Le nom de l'école, corrigeable sur place.
 *
 * ── Ce qu'il ne touche PAS ──────────────────────────────────────────────────
 * L'adresse de l'espace. C'est par elle qu'on entre, et la changer casserait
 * les signets de toute l'équipe et les liens des invitations en cours. L'écran
 * le dit, parce que la question se pose forcément en renommant.
 */
function NomDeLEcole({
  nom, modifiable, surEnregistrement,
}: {
  nom: string | null;
  modifiable: boolean;
  surEnregistrement: (nom: string) => Promise<void>;
}) {
  const [saisie, setSaisie] = useState('');
  const [etat, setEtat] = useState<'repos' | 'attente' | 'enregistre'>('repos');
  const [erreur, setErreur] = useState<string | null>(null);
  const minuteur = useRef<ReturnType<typeof setTimeout> | null>(null);
  const aEnvoyer = useRef<string | null>(null);
  const rappel = useRef(surEnregistrement);
  rappel.current = surEnregistrement;
  const connu = useRef(nom);
  connu.current = nom;

  // Part de l'enregistré dès qu'il arrive, et n'écrase plus ensuite : rien
  // n'est plus déroutant qu'un champ qui se réécrit sous les doigts.
  useEffect(() => { if (nom !== null && aEnvoyer.current === null) setSaisie(nom); }, [nom]);

  /*
   * Enregistré en frappant, comme le reste de l'application : il fallait
   * cliquer « Enregistrer », et un nom corrigé puis la page quittée était
   * perdu sans un mot. Un nom VIDE n'est pas envoyé : l'école garde le sien.
   */
  async function envoyer() {
    if (minuteur.current) clearTimeout(minuteur.current);
    minuteur.current = null;
    const valeur = aEnvoyer.current?.trim() ?? null;
    aEnvoyer.current = null;
    if (valeur === null || valeur === '' || valeur === connu.current) {
      setEtat('repos');
      return;
    }
    setErreur(null);
    try {
      await rappel.current(valeur);
      setEtat('enregistre');
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Enregistrement impossible');
      setEtat('repos');
    }
  }

  function surFrappe(valeur: string) {
    setSaisie(valeur);
    aEnvoyer.current = valeur;
    setEtat('attente');
    if (minuteur.current) clearTimeout(minuteur.current);
    minuteur.current = setTimeout(() => void envoyer(), 700);
  }

  // Quitter l'écran pendant le délai envoie la saisie au lieu de la perdre.
  useEffect(() => () => { if (aEnvoyer.current !== null) void envoyer(); }, []);

  return (
    <Carte titre="Nom de l'école">
      <div className="flex flex-col gap-3">
        {erreur && <Message ton="erreur">{erreur}</Message>}
        {nom === null ? (
          <Squelette className="h-9" />
        ) : modifiable ? (
          <>
            <Champ
              libelle="Intitulé affiché"
              value={saisie}
              onChange={(e) => surFrappe(e.target.value)}
              onBlur={() => void envoyer()}
              maxLength={120}
              aide="Il apparaît sur l’écran Équipe et dans les courriels d’invitation envoyés à vos collègues. L’adresse de connexion, elle, ne change pas."
            />
            <p className="text-[12px] text-[var(--color-fg-muted)]" aria-live="polite">
              {etat === 'attente' ? 'Enregistrement…' : etat === 'enregistre' ? 'Enregistré.' : 'Enregistré automatiquement.'}
            </p>
          </>
        ) : (
          <>
            <p className="text-sm font-medium">{nom}</p>
            <p className="text-[13px] text-[var(--color-fg-muted)]">
              Ce nom est fixé par la direction de l’école.
            </p>
          </>
        )}
      </div>
    </Carte>
  );
}
